﻿// Parts Finder Oil Guide â€” bundled embed module.
//
// Mounts the LiquiMoly OWW (Oil Wear Worldwide) widget in a Goldwagen-themed
// container. When LiquiMoly's UI renders oil recommendations, we listen for
// their `newPageEvent` jQuery event, scrape recommended product article numbers
// out of the rendered HTML, and ask our API which of those products are
// available at Goldwagen â€” then render an "Available at Goldwagen" panel
// underneath with matching SKUs (same pc-card layout as the parts-finder
// ResultsGrid + alternative-products slider).
//
// Target container (first hit wins):
//   #parts-finder-oil-guide  OR  [data-parts-finder-oil-guide]
//
// Per-instance config (read from the TARGET container, not the script tag):
//   data-client-id   LiquiMoly client id (default 5033)
//   data-country     ISO country (default ZAF)
//   data-language    ISO language (default ENG)
//   data-title       Heading for the matched-products panel
//                    (default "Available at Goldwagen")
//
// COMPLEX widget: preserves the LiquiMoly oww.js mount, the page-wide
// dropdown-hijack cleanup, the iframe-aware navigation, and per-call apiKey
// resolution. Only the generic helpers (config/apiKey/cookies/features/format)
// are swapped for shared imports.

import { getAppBaseUrl, getApiKey, inAppPartUrl } from '../shared/config.js';
import { addedToCartToast } from '../shared/cartToast.js';
import { esc, isContentEditor } from '../shared/dom.js';
import { getSessionRef, loadFeatures, isAnonymous } from '../shared/identity.js';
import { formatPriceIncl as formatPrice, formatPriceExcl, workshopLineHtml } from '../shared/format.js';

var _hostContainer = null;
var _widgetHost = null;       // <div id="oww"> that LiquiMoly mounts into
var _matchPanel = null;       // <div> we render the cross-link matches into
var _lastSeenSignature = '';  // de-dupe newPageEvent fires

var LM_BASE = 'https://openapi.liqui-moly.com';
var LM_BASE_PATH = '/api/v2/oww';
var LM_SCRIPT = LM_BASE + '/api/v2/public/js/oww.js';
var JQUERY_CDN = 'https://code.jquery.com/jquery-3.7.1.min.js';

// Overrides written after the matched-products fetch â€” same model as the
// alternatives slider: customer pricelist + real-time stock.
var _priceOverrides = {};
var _stockOverrides = {};
var _reducedFromOverrides = {}; // { sku: number } promo "was" price (excl-VAT)
var _standardPriceOverrides = {}; // { sku: number } Default/B2C/retail price (excl-VAT)
// Role-feature cache, populated from the shared loadFeatures() result. Used to
// gate showPriceComparison / showPrices.
var _features = {};

// Per-instance config now lives on the TARGET container (there's a single
// bundle <script> tag, so document.currentScript is not this widget's tag).
function dataAttr(name, fallback) {
	var map = { title: 'data-title', clientId: 'data-client-id', country: 'data-country', language: 'data-language' };
	var attr = map[name] || ('data-' + name);
	var v = _hostContainer ? _hostContainer.getAttribute(attr) : '';
	return (v && String(v).trim()) || fallback;
}

function _loginUrl() {
	try {
		var origin = (window.top && window.top.location && window.top.location.origin) || window.location.origin;
		var loc = (window.top && window.top.location) || window.location;
		// redirectTo must be a relative PATH (path + query), not the absolute URL.
		return origin + '/account/login?redirectTo=' + encodeURIComponent(loc.pathname + loc.search + loc.hash);
	} catch (e) {
		return '/account/login';
	}
}

function _loginPromptHtml() {
	return ''
		+ '<a href="' + esc(_loginUrl()) + '" class="pfog-login-for-pricing" '
		+ 'style="display:flex;align-items:center;gap:6px;padding:5px 8px;margin:4px 0 2px;'
		+ 'border:1px dashed rgba(0,22,137,.35);border-radius:6px;background:rgba(0,22,137,.05);'
		+ 'text-decoration:none;font:700 11px/1.2 -apple-system,BlinkMacSystemFont,sans-serif;'
		+ 'color:#001689;text-transform:uppercase;letter-spacing:.3px;">'
		+ '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" '
		+ 'stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto;"><path d="M12 11c0-1.1.9-2 2-2h4a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2v-6a2 2 0 012-2h4m4 0V7a4 4 0 10-8 0v4"/></svg>'
		+ '<span>Please login for pricing</span>'
		+ '<span style="margin-left:auto;color:rgba(0,22,137,.7);">Sign in &rarr;</span>'
		+ '</a>';
}

function loadScriptOnce(src, globalCheck) {
	return new Promise(function (resolve, reject) {
		if (globalCheck && globalCheck()) return resolve();
		// Already pending?
		var existing = document.querySelector('script[data-pfog-src="' + src + '"]');
		if (existing) {
			existing.addEventListener('load', function () { resolve(); });
			existing.addEventListener('error', function () { reject(new Error('Failed to load ' + src)); });
			return;
		}
		var s = document.createElement('script');
		s.src = src;
		s.async = true;
		s.setAttribute('data-pfog-src', src);
		s.addEventListener('load', function () { resolve(); });
		s.addEventListener('error', function () { reject(new Error('Failed to load ' + src)); });
		document.head.appendChild(s);
	});
}


/**
 * Make the Make / Model / Type dropdowns (and the jsMotorFilter dropdown)
 * behave as real click-to-open menus. LiquiMoly bakes class="dropdown show"
 * into the HTML and relies on Bootstrap JS we don't load â€” so every list
 * would sit permanently open without this. We toggle our own .pfog-open class
 * instead; CSS rules for .pfog-open are already in injectOwwStyles().
 * Safe to call repeatedly â€” guarded by $._pfogDropdownBound.
 */
function bindDropdownToggle($) {
	if (!$ || $._pfogDropdownBound) return;
	$._pfogDropdownBound = true;

	var closeAll = function () { $('#oww .dropdown.pfog-open').removeClass('pfog-open'); };

	$(document).on('click.pfogDropdown', '#oww .dropdown-toggle', function (e) {
		e.preventDefault();
		e.stopPropagation();
		var $dd = $(this).closest('.dropdown');
		var willOpen = !$dd.hasClass('pfog-open');
		closeAll();
		if (willOpen) $dd.addClass('pfog-open');
	});

	// Clicking a make/model/type item collapses the menu
	$(document).on('click.pfogDropdown', '#oww .jsDropdown .dropdown-menu .dropdown-item', function () {
		$(this).closest('.dropdown').removeClass('pfog-open');
	});

	// jsMotorFilter items: prevent href="" page reload; apply filter manually
	$(document).on('click.pfogDropdown', '#oww .jsMotorFilter .dropdown-menu .dropdown-item', function (e) {
		e.preventDefault();
		e.stopPropagation();
		var $item = $(this);
		var value = String($item.attr('data-value') || '');
		var label = $item.text().trim();
		var $dd = $item.closest('.dropdown');
		$dd.find('.dropdown-toggle').first().text(label);
		$dd.removeClass('pfog-open');
		if (!value) return;
		var $panelBody = $item.closest('.panel-body');
		var scope = $panelBody.length ? $panelBody : $('#oww');
		scope.find('.row.products').each(function () {
			var $row = $(this);
			var hasUsage = false;
			$row.find('.cases').each(function () { if ($(this).hasClass(value)) hasUsage = true; });
			$row.css('display', hasUsage ? '' : 'none');
		});
	});

	// Click outside closes all dropdowns
	$(document).on('click.pfogDropdownDoc', function (e) {
		if (!$(e.target).closest('#oww .dropdown').length) closeAll();
	});
}

/** Bootstrap-3-style accordion toggle, scoped to #oww. Bound once per page. */
function bindAccordionToggle($) {
	if (!$ || $._pfogAccordionBound) return;
	$._pfogAccordionBound = true;
	$(document).on('click.pfogAccordion', '#oww .panel-heading[data-toggle="collapse"]', function (e) {
		e.preventDefault();
		var $heading = $(this);
		var targetSel = $heading.attr('href');
		if (!targetSel) return;
		var $body = $(targetSel);
		if (!$body.length) return;
		var parentSel = $heading.attr('data-parent') || '#reco-accordion';
		var $parent = $(parentSel);
		var isOpen = $body.hasClass('in') || ($body.is(':visible') && $body.css('display') !== 'none');
		$parent.find('.panel-body.panel-collapse.in').not($body).removeClass('in').addClass('collapse').css('display', '');
		$parent.find('.panel-heading').not($heading).addClass('collapsed');
		if (isOpen) {
			$body.removeClass('in').addClass('collapse').css('display', '');
			$heading.addClass('collapsed');
		} else {
			// Drop `collapse` so the storefront's .collapse{display:none} can't hide it.
			$body.addClass('in').removeClass('collapse').css('display', '');
			$heading.removeClass('collapsed');
		}
	});
}

/** Sync .in/.collapsed initial state on each new LM page swap. */
function syncAccordionInitialState(host) {
	if (!host) return;
	var panels = host.querySelectorAll('.panel.panel-default');
	for (var i = 0; i < panels.length; i++) {
		var panel = panels[i];
		var heading = panel.querySelector('.panel-heading');
		var body = panel.querySelector('.panel-body.panel-collapse');
		if (!heading || !body) continue;
		var wantOpen = !heading.classList.contains('collapsed');
		body.style.display = '';
		if (wantOpen) { body.classList.add('in'); body.classList.remove('collapse'); }
		else { body.classList.remove('in'); body.classList.add('collapse'); }
	}
}

function resolvePrice(part) {
	var sku = part.sku || '';
	if (sku && typeof _priceOverrides[sku] === 'number') return _priceOverrides[sku];
	var n = Number(part.price);
	return !n || isNaN(n) ? 0 : n;
}

function resolveReducedFrom(part) {
	var sku = part.sku || '';
	if (sku && typeof _reducedFromOverrides[sku] === 'number') return _reducedFromOverrides[sku];
	return 0;
}

function resolveStandardPrice(part) {
	var sku = part.sku || '';
	if (sku && typeof _standardPriceOverrides[sku] === 'number') return _standardPriceOverrides[sku];
	return 0;
}

function resolveStockStatus(part) {
	var sku = part.sku || '';
	if (sku && _stockOverrides[sku]) return _stockOverrides[sku].status;
	return (typeof part.stock_status === 'string' ? part.stock_status : 'out_of_stock');
}

function fetchPricesAndStock(skus) {
	if (!skus || skus.length === 0) return Promise.resolve();
	var base = getAppBaseUrl().replace(/\/$/, '');
	var sessionRef = getSessionRef();
	var qs = '?skus=' + encodeURIComponent(skus.join(','));

	var pricesP = sessionRef
		? fetch(base + '/api/prices/customer' + qs, {
			method: 'GET',
			headers: { 'Accept': 'application/json', 'X-Session-Reference': sessionRef },
		})
			.then(function (r) { return r.ok ? r.json() : { prices: [] }; })
			.then(function (data) {
				(data.prices || []).forEach(function (p) {
					if (p && p.sku && typeof p.price === 'number') {
						_priceOverrides[p.sku] = p.price;
						if (typeof p.reducedFrom === 'number' && p.reducedFrom > p.price) {
							_reducedFromOverrides[p.sku] = p.reducedFrom;
						}
					}
				});
			})
			.catch(function () { /* non-critical */ })
		: Promise.resolve();

	// Standard / B2C / retail comparison prices Ã¢â‚¬â€ only fetched when the role
	// has showPriceComparison enabled (typical for B2B).
	var standardP = (_features && _features.showPriceComparison === true)
		? fetch(base + '/api/prices/standard' + qs, { method: 'GET', headers: { 'Accept': 'application/json' } })
			.then(function (r) { return r.ok ? r.json() : { prices: [] }; })
			.then(function (data) {
				(data.prices || []).forEach(function (p) {
					if (p && p.sku && typeof p.price === 'number') _standardPriceOverrides[p.sku] = p.price;
				});
			})
			.catch(function () { /* non-critical */ })
		: Promise.resolve();

	var stockHeaders = { 'Accept': 'application/json' };
	stockHeaders['Authorization'] = 'Bearer ' + getApiKey();
	var stockP = fetch(base + '/api/prices/stock' + qs, { method: 'GET', headers: stockHeaders })
		.then(function (r) { return r.ok ? r.json() : { stock: [] }; })
		.then(function (data) {
			(data.stock || []).forEach(function (s) {
				if (s && s.sku) _stockOverrides[s.sku] = { available: s.available, status: s.status };
			});
		})
		.catch(function () { /* non-critical */ });

	return Promise.all([pricesP, stockP, standardP]);
}

function buildCardHtml(part) {
	var id = part.id || part.sku || '';
	var sku = part.sku || '';
	var name = part.title || sku || 'Product';
	var brand = part.brand || '';
	var brandImg = part.manufacturerImage || '';
	var stockStatus = resolveStockStatus(part);
	var isIn = stockStatus === 'in_stock';
	var avail = isIn ? 'In Stock' : 'Out of Stock';
	var priceNum = resolvePrice(part);
	var priceIncl = formatPrice(priceNum);
	var priceExcl = formatPriceExcl(priceNum);
	var reducedFromExcl = resolveReducedFrom(part);
	var isPromo = priceNum > 0 && reducedFromExcl > priceNum;
	var reducedFromIncl = isPromo ? formatPrice(reducedFromExcl) : null;
	var standardExcl = resolveStandardPrice(part);
	var showComparison = (_features.showPriceComparison === true) && standardExcl > 0 && standardExcl !== priceNum;
	var standardIncl = showComparison ? formatPrice(standardExcl) : null;
	var imgUrl = part.imageUrl ? (part.imageUrl + '?height=262&width=262') : '';

	var h = '';
	h += '<article class="pc-card" data-id="' + esc(id) + '">';
	h += '<a href="#" class="pc-img-link pfog-nav-link" data-part-id="' + esc(id) + '">';
	h += '<div class="pc-img-wrap">';
	h += '<div class="pc-img" role="img" aria-label="' + esc(name || sku) + '" style="background-image:' + (imgUrl ? 'url(\'' + esc(imgUrl) + '\')' : 'none') + ';"></div>';
	h += '</div></a>';
	h += '<div class="pc-body">';
	if (brand) {
		h += '<div class="pc-brand">';
		if (brandImg) h += '<img src="' + esc(brandImg) + '" alt="' + esc(brand) + '" loading="lazy">';
		else h += '<span class="pc-brand-text">' + esc(brand) + '</span>';
		h += '</div>';
	}
	if (part._matchedFrom) {
		h += '<div class="pfog-matched-from">For: ' + esc(part._matchedFrom) + '</div>';
	}
	h += '<a href="#" class="pc-name-link pfog-nav-link" data-part-id="' + esc(id) + '"><h3 class="pc-name">' + esc(name) + '</h3></a>';
	if (sku) h += '<p class="pc-sku">Item code: ' + esc(sku) + '</p>';
	if (_features.showPrices === false && isAnonymous()) {
		h += _loginPromptHtml();
	} else if (_features.showPrices !== false && standardIncl && priceNum > 0) {
		/* Price comparison: the normal price block with the RETAIL amounts, and
		   the customer's own price on a small grey "Workshop:" line below it. */
		h += '<div class="pc-price-row" style="flex-wrap:wrap;">';
		h += '<span class="pc-price">' + esc(standardIncl) + '</span><span class="pc-price-tax">incl. VAT</span>';
		h += '</div>';
		h += '<div class="pc-price-excl">' + esc(formatPriceExcl(standardExcl)) + ' excl. VAT</div>';
		h += workshopLineHtml({ incl: priceIncl, excl: priceExcl, wasIncl: reducedFromIncl, isPromo: isPromo });
	} else if (_features.showPrices !== false) {
		/* Retail first, the customer's own (workshop) price below. */
		if (standardIncl) h += '<div style="font-size:11px;color:#6b7280;margin-bottom:2px;">Retail: <span style="color:#9ca3af">' + esc(standardIncl) + '</span> <span style="color:#9ca3af;font-size:10px;">incl. VAT</span></div>';
		h += '<div class="pc-price-row" style="flex-wrap:wrap;">';
		if (priceIncl) {
			h += '<span class="pc-price">' + esc(priceIncl) + '</span><span class="pc-price-tax">incl. VAT</span>';
			if (reducedFromIncl) {
				h += '<span style="margin-left:6px;color:#9ca3af;font-size:11px;" title="Regular price">' + esc(reducedFromIncl) + '</span>';
			}
			if (isPromo) {
				h += '<span style="margin-left:6px;padding:1px 6px;border-radius:9999px;background:#fee2e2;color:#b91c1c;font-size:10px;font-weight:700;letter-spacing:.3px;">ON PROMO</span>';
			}
		} else {
			h += '<span class="pc-price">Price on request</span>';
		}
		h += '</div>';
		if (priceExcl) h += '<div class="pc-price-excl">' + esc(priceExcl) + ' excl. VAT</div>';
	}
	h += '<div class="pc-availability"><span class="pc-avail-dot ' + (isIn ? 'in' : 'out') + '" aria-hidden="true"></span><span class="pc-avail-label">' + esc(avail) + '</span></div>';
	h += '</div>';
	h += '<div class="pc-actions"><div class="pc-btn-row">';
	h += '<a href="#" class="pc-btn-details pfog-nav-link" data-part-id="' + esc(id) + '">Details</a>';
	h += '<button type="button" class="pc-btn-cart pfog-cart-btn" data-sku="' + esc(sku) + '" data-name="' + esc(name) + '" aria-label="Add ' + esc(name) + ' to cart" title="Add to cart"><span class="material-symbols-outlined">add_shopping_cart</span></button>';
	h += '</div></div>';
	h += '</article>';
	return h;
}

// Navigate the visitor to the OE Commerce storefront product page for a
// part, resolving the URL via the same /storefront-url endpoint the
// parts-finder ResultsGrid uses. When this embed runs inside an iframe we
// must drive window.top â€” otherwise the storefront product page would load
// inside the widget's own iframe instead of taking over the tab (which read
// as "it went to the parts-finder page" rather than the product page).
function _goToUrl(url) {
	try {
		if (window.self !== window.top && window.top) { window.top.location.href = url; return; }
	} catch (e) { /* cross-origin parent â€” fall through to own window */ }
	window.location.href = url;
}
function navigateToPart(partId) {
	if (!partId) return;
	var base = getAppBaseUrl().replace(/\/$/, '');
	var apiUrl = base + '/api/parts/' + encodeURIComponent(partId) + '/storefront-url';
	var fallbackUrl = inAppPartUrl(partId);
	var headers = { 'Accept': 'application/json' };
	headers['Authorization'] = 'Bearer ' + getApiKey();
	fetch(apiUrl, { method: 'GET', headers: headers })
		.then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
		.then(function (result) {
			var url = (result.data && result.data.url) ? String(result.data.url).trim() : '';
			_goToUrl(url || fallbackUrl);
		})
		.catch(function () { _goToUrl(fallbackUrl); });
}

function addToCart(sku, name, btn) {
	if (!sku) return;
	// Credit gate â€” see storefront-credit-guard.js. Degrades to proceeding
	// when the guard script isn't present.
	if (window.PFCreditGuard && window.PFCreditGuard.guard) {
		window.PFCreditGuard.guard({ sku: sku, qty: 1 }).then(function (ok) { if (ok) _addToCartNow(sku, name, btn); });
		return;
	}
	_addToCartNow(sku, name, btn);
}
function _addToCartNow(sku, name, btn) {
	if (!sku) return;
	var base = getAppBaseUrl().replace(/\/$/, '');
	var cartUrl = base + '/api/cart/add';
	var headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
	headers['Authorization'] = 'Bearer ' + getApiKey();
	btn.disabled = true;
	btn.innerHTML = '<span style="display:inline-block;width:14px;height:14px;border:2px solid rgba(255,255,255,.5);border-top-color:#fff;border-radius:50%;animation:pfog-spin .7s linear infinite;"></span>';
	fetch('/api/storefront/cart', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ lineItems: [{ quantity: 1, sku: sku }] }),
	}).then(function (r) {
		if (r.ok) {
			btn.innerHTML = '<span class="material-symbols-outlined">check</span>';
			setTimeout(function () { btn.disabled = false; btn.innerHTML = '<span class="material-symbols-outlined">add_shopping_cart</span>'; }, 2000);
			var win = window;
			try { if (window.top) win = window.top; } catch (e) {}
			if (win.Cart && win.Cart.MiniCart && win.Cart.MiniCart.GetMiniCart) win.Cart.MiniCart.GetMiniCart();
			if (win.Cart && win.Cart.UpdateCartCount) win.Cart.UpdateCartCount();
			if (win.Utils && typeof win.Utils.Toastr === 'function') win.Utils.Toastr(addedToCartToast(name, 1));
		} else { throw new Error('sf cart ' + r.status); }
	}).catch(function () {
		fetch(cartUrl, { method: 'POST', headers: headers, body: JSON.stringify({ sku: sku, quantity: 1 }) })
			.then(function (r2) {
				btn.disabled = false;
				btn.innerHTML = r2.ok ? '<span class="material-symbols-outlined">check</span>' : '<span class="material-symbols-outlined">add_shopping_cart</span>';
				if (r2.ok) setTimeout(function () { btn.innerHTML = '<span class="material-symbols-outlined">add_shopping_cart</span>'; }, 2000);
			})
			.catch(function () { btn.disabled = false; btn.innerHTML = '<span class="material-symbols-outlined">add_shopping_cart</span>'; });
	});
}

// Extract recommended products from the LiquiMoly recommendation page.
// Their tiles render anchors with `?open=LM1862&stext=LM1862` Ã¢â‚¬â€ that
// LMxxxx is the LiquiMoly article number and (importantly) maps directly
// onto Goldwagen SKUs, so we get a hard match in /api/oil/match.
function _articleFromHref(href) {
	if (!href) return '';
	var m = href.match(/[?&](?:open|stext)=([A-Za-z0-9-]+)/);
	return m ? m[1].toUpperCase() : '';
}
function extractLiquiMolyProducts() {
	if (!_widgetHost) return [];
	var results = [];
	var seen = {};

	// Primary: recommendation tiles. LM markup varies â€” legacy `.product-tile`
	// (name in `.product-name a`) and current `.product-card` (name in
	// `.product-item-name`, link `a.product-item-info` with ?open=LMxxxx).
	var tiles = _widgetHost.querySelectorAll('.product-tile, .product-card');
	for (var i = 0; i < tiles.length; i++) {
		var tile = tiles[i];
		var nameLink = tile.querySelector(
			".product-name a, .hoverLink, a.product-image, a.product-item-info, a[href*='open='], a[href*='stext=']"
		);
		var nameEl = tile.querySelector('.product-item-name, .product-name a, .hoverLink');
		var name = (nameEl && nameEl.textContent || '').trim();
		if (!name && nameLink) name = (nameLink.getAttribute('title') || '').trim();
		if (!name && nameLink) name = (nameLink.textContent || '').trim();
		if (!name) continue;
		var articleNumber = _articleFromHref(nameLink && nameLink.getAttribute('href'));
		if (!articleNumber) {
			var anyLink = tile.querySelector("a[href*='open='], a[href*='stext=']");
			articleNumber = _articleFromHref(anyLink && anyLink.getAttribute('href'));
		}
		var key = name.toLowerCase() + '|' + articleNumber.toLowerCase();
		if (seen[key]) continue;
		seen[key] = true;
		results.push({ name: name, articleNumber: articleNumber });
	}

	// Fallback for the older wizard-style article-card layout
	if (results.length === 0) {
		var candidates = _widgetHost.querySelectorAll('.productName, .articleName, .articleTitle, a[href*="article/"]');
		for (var j = 0; j < candidates.length; j++) {
			var el = candidates[j];
			var nm = (el.textContent || '').trim();
			if (!nm || nm.length < 4 || nm.length > 120) continue;
			var an = '';
			var anEl = el.parentElement && el.parentElement.querySelector('.articleNumber, [class*="articleNumber"], [class*="artNr"]');
			if (anEl) an = (anEl.textContent || '').replace(/[^0-9A-Z-]/gi, '').trim();
			var k = nm.toLowerCase() + '|' + an.toLowerCase();
			if (seen[k]) continue;
			seen[k] = true;
			results.push({ name: nm, articleNumber: an });
		}
	}
	return results;
}

/**
 * Neutralise LM product-detail links. Their hrefs point at the legacy
 * `partsfinder.goldwagen.com` site (target="_self"), which we never want to
 * reach. We stash the LM article number and replace the href with
 * `javascript:void(0)` so it can't navigate by any means; the click handler
 * (installNavGuard) then scrolls the visitor to the Goldwagen match panel.
 */
function rewriteProductLinkTargets() {
	if (!_widgetHost) return;
	var links = _widgetHost.querySelectorAll('.product-tile a[href], .product-card a[href]');
	for (var i = 0; i < links.length; i++) {
		var a = links[i];
		var article = _articleFromHref(a.getAttribute('href'));
		if (article) a.setAttribute('data-pfog-article', article);
		a.setAttribute('href', 'javascript:void(0)');
		a.removeAttribute('target');
		a.removeAttribute('rel');
		a.style.cursor = 'pointer';
	}
}

// Build a stable signature of the current recommendations so we don't
// re-fetch matches on every newPageEvent (the widget fires it for navigation too).
function buildSignature(products) {
	return products.map(function (p) { return (p.articleNumber || p.name).toLowerCase(); }).sort().join('|');
}

function renderMatchPanelLoading() {
	if (!_matchPanel) return;
	var title = dataAttr('title', 'Available at Goldwagen');
	_matchPanel.innerHTML =
		'<h3 class="pfog-section-title">' + esc(title) + '</h3>' +
		'<p class="pfog-section-sub">Matching the recommended products with our stock...</p>' +
		'<div class="pfog-loading">Loading...</div>';
}

function renderMatchPanelEmpty() {
	if (!_matchPanel) return;
	_matchPanel.innerHTML = ''; // hide the panel entirely when nothing matches
}

function renderMatchPanelResults(matches) {
	if (!_matchPanel) return;
	var title = dataAttr('title', 'Available at Goldwagen');
	// Flatten {recommendation: <lm name>, parts: [...]}
	var allParts = [];
	matches.forEach(function (m) {
		(m.parts || []).forEach(function (p) {
			p._matchedFrom = m.recommendation || '';
			allParts.push(p);
		});
	});
	// Dedupe by SKU keeping the first occurrence (preserving order of LiquiMoly recommendations)
	var seen = {};
	allParts = allParts.filter(function (p) {
		var k = (p.sku || p.id || '').toLowerCase();
		if (!k || seen[k]) return false;
		seen[k] = true;
		return true;
	});
	if (allParts.length === 0) {
		renderMatchPanelEmpty();
		return;
	}

	var skus = allParts.map(function (p) { return p.sku; }).filter(Boolean);
	// Load role flags FIRST so fetchPricesAndStock knows whether to also
	// request /api/prices/standard for the B2C comparison line.
	loadFeatures().then(function (f) { _features = f || {}; return fetchPricesAndStock(skus); }).then(function () {
		var html = '<h3 class="pfog-section-title">' + esc(title) + '</h3>';
		html += '<p class="pfog-section-sub">Goldwagen stocks the following products that match the recommendation:</p>';
		html += '<div class="pfog-grid product-categories-contain pc-grid">';
		allParts.forEach(function (p) { html += buildCardHtml(p); });
		html += '</div>';
		_matchPanel.innerHTML = html;

		_matchPanel.querySelectorAll('.pfog-nav-link').forEach(function (el) {
			el.addEventListener('click', function (e) {
				e.preventDefault();
				navigateToPart(el.getAttribute('data-part-id'));
			});
		});
		_matchPanel.querySelectorAll('.pfog-cart-btn').forEach(function (btn) {
			btn.addEventListener('click', function (e) {
				e.preventDefault();
				addToCart(btn.getAttribute('data-sku'), btn.getAttribute('data-name') || '', btn);
			});
		});
	});
}

function onWidgetUpdate() {
	if (!_widgetHost) return;
	var products = extractLiquiMolyProducts();
	if (products.length === 0) {
		renderMatchPanelEmpty();
		_lastSeenSignature = '';
		return;
	}
	var sig = buildSignature(products);
	if (sig === _lastSeenSignature) return;
	_lastSeenSignature = sig;

	renderMatchPanelLoading();

	var base = getAppBaseUrl().replace(/\/$/, '');
	var headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
	headers['Authorization'] = 'Bearer ' + getApiKey();
	fetch(base + '/api/oil/match', {
		method: 'POST',
		headers: headers,
		body: JSON.stringify({ products: products }),
	})
		.then(function (r) { return r.ok ? r.json() : { matches: [] }; })
		.then(function (data) { renderMatchPanelResults(data.matches || []); })
		.catch(function () { renderMatchPanelEmpty(); });
}

function buildHostMarkup() {
	_hostContainer.innerHTML = '';

	var wrap = document.createElement('div');
	wrap.className = 'pfog-wrap';

	var widgetHost = document.createElement('div');
	widgetHost.className = 'pfog-widget-host';

	// Branded header â€” matches OilGuideModal.tsx dark navy bar
	var logoUrl = getAppBaseUrl() + 'liqui-moly-logo.png';
	var header = document.createElement('div');
	header.className = 'pfog-header';
	header.innerHTML = ''
		+ '<div class="pfog-header-inner">'
		+ '<div class="pfog-logo-wrap">'
		+ '<img src="' + esc(logoUrl) + '" alt="Liqui Moly" class="pfog-logo-img">'
		+ '</div>'
		+ '<div class="pfog-header-text">'
		+ '<span class="pfog-header-title">Oil Guide</span>'
		+ '<span class="pfog-header-sub">Recommended oils &amp; fluids for your vehicle</span>'
		+ '</div>'
		+ '</div>';
	var redStrip = document.createElement('div');
	redStrip.className = 'pfog-header-strip';

	widgetHost.appendChild(header);
	widgetHost.appendChild(redStrip);

	var oww = document.createElement('div');
	oww.id = 'oww';
	oww.innerHTML = '<div class="pfog-loading">Loading oil guide...</div>';
	widgetHost.appendChild(oww);

	var matchPanel = document.createElement('div');
	matchPanel.className = 'pfog-match-panel';

	wrap.appendChild(widgetHost);
	wrap.appendChild(matchPanel);
	_hostContainer.appendChild(wrap);

	_widgetHost = oww;
	_matchPanel = matchPanel;
}

// Defensive: cancel any anchor click inside the widget host whose href points
// at LiquiMoly's /api/v1/oww/... endpoints Ã¢â‚¬â€ the browser would otherwise
// resolve them against our origin and the parts-finder route would catch
// them, redirecting to /parts-finder. LiquiMoly's plugin handles these via
// jQuery delegation, but a capture-phase listener guards against any race.
function installNavGuard() {
	if (!_widgetHost) return;
	_widgetHost.addEventListener('click', function (e) {
		var anchor = e.target && e.target.closest ? e.target.closest('a') : null;
		if (!anchor) return;
		// Recommended-product tile links are neutralised (see
		// rewriteProductLinkTargets) so they never reach the legacy
		// partsfinder.goldwagen.com site. Swallow the click and bring the
		// visitor to the Goldwagen matches shown in the panel below.
		if (anchor.closest('.product-tile, .product-card')) {
			e.preventDefault();
			e.stopPropagation();
			try {
				if (_matchPanel && _matchPanel.firstChild) {
					_matchPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			} catch (err) { /* ignore */ }
			return;
		}
		var href = anchor.getAttribute('href') || anchor.getAttribute('data-link') || '';
		if (!href) return;
		if (!/\/api\/v\d+\/oww\//.test(href) && !/openapi\.liqui-moly\.com/.test(href)) return;
		e.preventDefault();
		e.stopPropagation();
		var $ = window.jQuery;
		if ($ && _widgetHost) {
			var $host = $(_widgetHost);
			var pluginData = $host.data && $host.data('plugin_oww');
			if (pluginData && typeof pluginData.ajaxLoad === 'function') {
				try { pluginData.ajaxLoad(href); return; } catch (err) { /* fall through */ }
			}
			setTimeout(function () {
				try { $(anchor).trigger('click'); } catch (err) { /* ignore */ }
			}, 0);
		}
	}, true);
}

/**
 * Undo LiquiMoly's global dropdown hijack.
 *
 * oww.js (their CDN plugin, which we can't edit) runs on every render:
 *     jQuery(".dropdown-toggle").unbind().on("click", <its handler>)
 * That selector is PAGE-WIDE, so it strips and rebinds every Bootstrap
 * .dropdown-toggle on the host storefront (main nav, account menu, mini
 * cart, ...). Its handler only toggles an `.open` class and preventDefault()s,
 * which collides with the storefront's Bootstrap 3 dropdowns and leaves them
 * dead ("some dropdowns stop working" â€” the reported bug).
 *
 * Bootstrap 3 wires dropdowns via a DELEGATED handler on `document`, not on
 * the toggle elements, so removing the direct click handler oww just bound to
 * every toggle OUTSIDE #oww fully restores the storefront's own behaviour.
 * oww's rebind is the only direct click handler left on those elements after
 * its own `.unbind()`, so `.off('click')` here targets exactly it. Toggles
 * inside #oww are left alone (handled by bindDropdownToggle + oww).
 */
function restoreParentDropdowns() {
	var $ = window.jQuery;
	if (!$) return;
	try {
		$('.dropdown-toggle').not('#oww .dropdown-toggle').off('click');
	} catch (e) { /* ignore */ }
}

var _pfogParentGuardInstalled = false;
/**
 * Timing-independent backstop for restoreParentDropdowns.
 *
 * oww rebinds its global .dropdown-toggle handler at unpredictable moments â€” on
 * every render AND again as the modal opens/closes. A cleanup tied only to
 * newPageEvent misses the bind that happens on CLOSE, so the storefront's
 * dropdowns stay dead after the oil-guide modal is dismissed (the reported bug).
 *
 * This installs a ONE-TIME capture-phase document click listener: the instant a
 * parent (non-#oww) .dropdown-toggle is clicked, we strip oww's hijacking handler
 * BEFORE it fires â€” capture runs on the way down, before the element's own
 * bubble-phase jQuery handler â€” so the storefront's document-delegated Bootstrap
 * handler then toggles the menu normally. Self-heals no matter when oww last
 * rebound, and survives the modal being opened and closed repeatedly.
 */
function installParentDropdownGuard() {
	if (_pfogParentGuardInstalled) return;
	_pfogParentGuardInstalled = true;
	document.addEventListener('click', function (e) {
		var t = e.target;
		var toggle = t && t.closest ? t.closest('.dropdown-toggle') : null;
		if (!toggle) return;
		if (toggle.closest('#oww')) return; // the widget's own toggles â€” leave alone
		// Strip both oww hijacks BEFORE the event reaches their handlers (capture
		// runs first): the per-element .dropdown-toggle handler AND the page-wide
		// document.click dropdown-closer. The storefront's delegated Bootstrap
		// handler then toggles the menu normally.
		restoreParentDropdowns();
		removeOwwDocClickHandlers(window.jQuery);
	}, true);
}

// oww also binds PERSISTENT, page-wide handlers on `document` itself â€” not just on
// .dropdown-toggle elements. The worst is `jQuery(document).click(...)` (oww.js
// ~line 497) which runs `jQuery(".dropdown").removeClass("open")` + stopPropagation
// on essentially every click, closing/blocking the storefront's OWN Bootstrap
// dropdowns. It stays bound after the oil-guide modal closes (so the parent nav
// stays dead) and oww re-adds it on every render (they stack). These handlers are
// anonymous and UN-namespaced. We snapshot document's click handlers just before
// oww initialises, then after each render strip any un-namespaced click handler oww
// added that wasn't in the baseline â€” leaving Bootstrap's (.bs.*) and our own
// (.pfog*) namespaced handlers untouched.
var _pfogDocClickBaseline = null;
function snapshotDocClickHandlers($) {
	try {
		var ev = ($._data && $._data(document, 'events')) || {};
		_pfogDocClickBaseline = (ev.click || []).map(function (h) { return h.handler; });
	} catch (e) { _pfogDocClickBaseline = null; }
}
function removeOwwDocClickHandlers($) {
	if (!_pfogDocClickBaseline || !$ || !$._data) return;
	try {
		var ev = $._data(document, 'events') || {};
		var list = (ev.click || []).slice();
		for (var i = 0; i < list.length; i++) {
			var entry = list[i];
			if (entry.namespace) continue; // keep Bootstrap's .bs.* and our .pfog* handlers
			if (_pfogDocClickBaseline.indexOf(entry.handler) !== -1) continue; // pre-existing
			$(document).off('click', entry.handler); // oww's page-wide dropdown-closer â€” remove
		}
	} catch (e) { /* ignore */ }
}

function mountLiquiMolyWidget() {
	var $ = window.jQuery;
	if (!$ || !$.fn || !$.fn.oww) {
		console.warn('PartsOilGuide: LiquiMoly plugin not present after script load');
		_widgetHost.innerHTML = '<div class="pfog-loading">Could not load oil guide.</div>';
		return;
	}
	var clientId = dataAttr('clientId', '5033');
	var country = dataAttr('country', 'ZAF');
	var language = dataAttr('language', 'ENG');
	// Baseline document click handlers BEFORE oww binds any of its own.
	snapshotDocClickHandlers($);
	try {
		// _baseUrl + _basePath are REQUIRED Ã¢â‚¬â€ they default to null and the
		// plugin would otherwise issue JSONP calls against the host page
		// origin (producing 404s like /0/5033/ZAF/ENG/1/).
		$(_widgetHost).oww({
			_baseUrl: LM_BASE,
			_basePath: LM_BASE_PATH,
			_clientId: clientId,
			_countryCode: country,
			_languageCode: language,
		});
	} catch (e) {
		console.error('PartsOilGuide: oww init failed', e);
		_widgetHost.innerHTML = '<div class="pfog-loading">Could not initialise oil guide.</div>';
		return;
	}

	installNavGuard();
	bindDropdownToggle($);
	bindAccordionToggle($);
	// Capture-phase guard survives modal open/close â€” must be installed once the
	// widget (and thus oww) is active, so oww's global rebinds are always undone
	// on the next parent-dropdown click regardless of render/close timing.
	installParentDropdownGuard();

	// oww rebinds its global .dropdown-toggle handler AND its page-wide
	// document.click dropdown-closer during its initial render (which may land
	// before or without a newPageEvent), so sweep a few times after mount.
	var sweepParentDropdowns = function () { restoreParentDropdowns(); removeOwwDocClickHandlers($); };
	sweepParentDropdowns();
	setTimeout(sweepParentDropdowns, 150);
	setTimeout(sweepParentDropdowns, 600);
	setTimeout(sweepParentDropdowns, 1500);

	$(document).on('newPageEvent', function () {
		// oww re-hijacks page-wide .dropdown-toggle AND re-adds its document.click
		// dropdown-closer on every render â€” undo both immediately so the
		// storefront's own dropdowns keep working.
		sweepParentDropdowns();
		setTimeout(function () {
			sweepParentDropdowns();
			syncAccordionInitialState(_widgetHost);
			rewriteProductLinkTargets();
			onWidgetUpdate();
		}, 50);
	});
}

function resolveHostContainer() {
	return document.getElementById('parts-finder-oil-guide')
		|| document.querySelector('[data-parts-finder-oil-guide]');
}

export function init() {
	// Do nothing in the page builder / backoffice â€” no widget, no jQuery load,
	// no global handlers. Keeps the editor's own Bootstrap chrome working.
	if (isContentEditor()) return;

	_hostContainer = resolveHostContainer();
	if (!_hostContainer) return; // not on a page with this widget

	buildHostMarkup();

	// NB: we deliberately do NOT load Bootstrap globally â€” its base selectors
	// restyle the host storefront page (header etc.). injectOwwStyles() ships a
	// Bootstrap-grid + visibility shim scoped under #oww instead.
	// NB: the Bearer key is resolved per-call via getApiKey() (init may run
	// before parts-finder-embed.js publishes the shared key global).
	loadScriptOnce(JQUERY_CDN, function () { return typeof window.jQuery !== 'undefined'; })
		.then(function () {
			return loadScriptOnce(LM_SCRIPT, function () {
				return typeof window.jQuery !== 'undefined' && !!window.jQuery.fn && !!window.jQuery.fn.oww;
			});
		})
		.then(function () { mountLiquiMolyWidget(); })
		.catch(function (err) {
			console.error('PartsOilGuide: dependency load failed', err);
			if (_widgetHost) _widgetHost.innerHTML = '<div class="pfog-loading">Could not load oil guide. Please refresh and try again.</div>';
		});
}

export var id = 'oil-guide';
